/* eslint-disable max-len */
import { ChatUserRoleDto } from '../../apis/nhe-ai-chat-service/nhe-ai-chat-service.dtos';
import { IconPrefixes } from '../../constants/hmds.constants';

export enum PromptExampleType {
  GENERATE_QUESTION = 'GENERATE_QUESTION',
  SUMMARY = 'SUMMARY',
  FLASHCARD = 'FLASHCARD',
  CASE_STUDY = 'CASE_STUDY',
  LECTURE_OUTLINE = 'LECTURE_OUTLINE',
}

export type PromptExampleConfig = {
  type: PromptExampleType;
  userRole: ChatUserRoleDto;
  title: string;
  cardText: string;
  fullPrompt: string;
  iconName: string;
  iconPrefix: string;
  iconClass: string;
};

export const PromptExampleConfigs: PromptExampleConfig[] = [
  {
    type: PromptExampleType.GENERATE_QUESTION,
    userRole: ChatUserRoleDto.STUDENT,
    title: 'Practice with a quiz',
    cardText: 'Check understanding with a short quiz and feedback.',
    fullPrompt: 'Quiz me on the current topic or material I\'m studying. If you don\'t have enough context, ask what I want to practice.',
    iconName: 'help-outline-circle',
    iconPrefix: IconPrefixes.GIZMO,
    iconClass: 'u-els-color-extended-green-5'
  },
  {
    type: PromptExampleType.SUMMARY,
    userRole: ChatUserRoleDto.STUDENT,
    title: 'Break down a concept',
    cardText: 'Clarify the main idea, key details, and why it matters.',
    fullPrompt: 'Explain the current concept, subject, or problem. If there isn\'t enough context, ask what concept, subject, or problem to explain.',
    iconName: 'alignment',
    iconPrefix: IconPrefixes.GIZMO,
    iconClass: 'u-els-color-extended-pink-8'
  },
  {
    type: PromptExampleType.FLASHCARD,
    userRole: ChatUserRoleDto.STUDENT,
    title: 'Make flashcards',
    cardText: 'Turn key concepts into flashcards.',
    fullPrompt: 'Create flashcards from the current content, concept, or material I\'m studying. If you don\'t have enough context, ask what content I want turned into flashcards.',
    iconName: 'column-middle',
    iconPrefix: IconPrefixes.GIZMO,
    iconClass: 'u-els-color-extended-teal-8'
  },
  {
    type: PromptExampleType.GENERATE_QUESTION,
    userRole: ChatUserRoleDto.INSTRUCTOR,
    title: 'Create practice questions',
    cardText: 'Generate questions that support application and understanding.',
    fullPrompt: 'Create practice questions based on the current topic, lesson, or content. If you don\'t have enough context, ask what topic or competency the questions should address. Include answers, rationales, and notes about common misconceptions.',
    iconName: 'help-outline-circle',
    iconPrefix: IconPrefixes.GIZMO,
    iconClass: 'u-els-color-extended-green-5'
  },
  {
    type: PromptExampleType.CASE_STUDY,
    userRole: ChatUserRoleDto.INSTRUCTOR,
    title: 'Build a case study',
    cardText: 'Build a clinical case for learner analysis and discussion.',
    fullPrompt: 'Create a clinical case study based on the current topic, objective, or content. If you don\'t have enough context, ask for the topic, learner level, and clinical judgment focus. Include patient background, key cues, decision points, student questions, suggested answers, rationales, and instructor facilitation notes.',
    iconName: 'case-study',
    iconPrefix: IconPrefixes.HMDS,
    iconClass: 'u-els-color-extended-teal-8'
  },
  {
    type: PromptExampleType.LECTURE_OUTLINE,
    userRole: ChatUserRoleDto.INSTRUCTOR,
    title: 'Plan a lesson',
    cardText: 'Draft a lesson plan with objectives, activities, and checks for learning.',
    fullPrompt: 'Use the current course, topic, objective, or content if available to draft a lesson plan. If you don\'t have enough context, ask for the topic, learner level, and time available. Include objectives, teaching activities, checks for understanding, and practice opportunities.',
    iconName: 'alignment',
    iconPrefix: IconPrefixes.GIZMO,
    iconClass: 'u-els-color-extended-pink-8'
  },
];

export const getPromptExampleConfigsForRole = (userRole: ChatUserRoleDto): PromptExampleConfig[] =>
  PromptExampleConfigs.filter((config) => config.userRole === userRole);

export const getPromptExampleConfig = (
  type: PromptExampleType,
  userRole: ChatUserRoleDto
): PromptExampleConfig | undefined =>
  PromptExampleConfigs.find((config) => config.type === type && config.userRole === userRole);
